import {filter, find, map, cloneDeep} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {userSettingsApi} from 'api/user-settings';
import {
    Button, FilesManagerInput, PredefinedNotifierType, Table,
    TableBody, TableCell, TableRow, UnsavedChangesLeavePrompt
} from 'components';
import {FileArea, FileOwner} from 'components/files-manager/types';
import {useApiData} from 'hooks';
import {CustomerLogo} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.CUSTOMER_LOGO;

export const ClientSessionCustomerLogo = () => {
    const {t} = useTranslation();
    const [accountCustomerLogo, setAccountCustomerLogo] = useState<CustomerLogo>(null);
    const [groupCustomerLogo, setGroupCustomerLogo] = useState<CustomerLogo[]>(null);
    const [initialState, setInitialState] = useState<{account: string, groups: string[]}>();
    const tableHeaders = useMemo(() => [t(T_KEYS.TABLE_HEADERS.GROUP), t(T_KEYS.TABLE_HEADERS.LOGO_URL)], [t]);

    const {
        send: fetchClientSessionCustomerLogo,
        data,
        loading: fetchInProgress
    } = useApiData(userSettingsApi.getClientSessionCustomerLogo, {notificationType: PredefinedNotifierType.FetchError});

    const areLogoEquals = useCallback((path1, path2) => !path1 && !path2 || path1 === path2, []);

    const accountCustomerLogoUpdated = useMemo(() =>
        !areLogoEquals(data?.account.image?.path, accountCustomerLogo?.image?.path), [data, accountCustomerLogo, areLogoEquals]);

    const updatedGroups = useMemo(() => filter(groupCustomerLogo, (group) => {
        const oldGroup = find(data.groups, {groupId: group.groupId}) as CustomerLogo;
        return !areLogoEquals(oldGroup.image?.path, group.image?.path);
    }), [data, groupCustomerLogo, areLogoEquals]);

    const saveChangesFn = useCallback(async () => {
        await userSettingsApi.updateClientSessionCustomerLogo({
            account: accountCustomerLogoUpdated ? accountCustomerLogo : null,
            groups: updatedGroups
        });
        return fetchClientSessionCustomerLogo();
    }, [accountCustomerLogo, accountCustomerLogoUpdated, updatedGroups, fetchClientSessionCustomerLogo]);

    const {
        send: saveChanges,
        loading: saveInProgress
    } = useApiData(saveChangesFn, {notificationType: PredefinedNotifierType.Save});

    const handleAccountCustomerLogoUpdate = useCallback((value: string) => {
        setAccountCustomerLogo({
            image: {path: value}
        });
    }, [setAccountCustomerLogo]);

    const handleGroupCustomerLogoUpdate = useCallback((groupId: string, value: string) => {
        setGroupCustomerLogo((prev) => map(prev, (group) => {
            if (group.groupId === groupId) {
                group.image = {path: value};
            }
            return group;
        }));
    }, []);

    const hasNoChanges = useMemo(
        () => !(accountCustomerLogoUpdated || updatedGroups?.length),
        [accountCustomerLogoUpdated, updatedGroups]
    );

    const groupPaths = useMemo(() => map(groupCustomerLogo, ({image}) => image?.path ?? ''), [groupCustomerLogo]);

    useEffect(() => {
        setAccountCustomerLogo(data?.account);
        setGroupCustomerLogo(cloneDeep(data?.groups));
        setInitialState({account: data?.account?.image?.path ?? '', groups: map(data?.groups, ({image}) => image?.path ?? '')});
    }, [data]);

    useEffect(() => {
        fetchClientSessionCustomerLogo();
    }, [fetchClientSessionCustomerLogo]);

    return (
        <Fragment>
            <div className='tw-text-right'>
                <Button
                    width='small'
                    color='primary'
                    disabled={hasNoChanges}
                    onClick={saveChanges}
                >
                    {t(T_KEYS.SAVE_BTN)}
                </Button>
            </div>
            <Table className='tw-my-4' headerNames={tableHeaders} loading={fetchInProgress || saveInProgress}>
                <TableBody>
                    <Fragment>
                        <TableRow>
                            <TableCell>{t(T_KEYS.ACCOUNT)}</TableCell>
                            <TableCell>
                                <FilesManagerInput
                                    filesManagerProps={{
                                        area: FileArea.ClientSession,
                                        ownerType: FileOwner.Account,
                                    }}
                                    inputProps={{
                                        name: 'account-customer-logo',
                                        transparent: true,
                                        value: accountCustomerLogo?.image?.path || '',
                                        onChange: handleAccountCustomerLogoUpdate,
                                    }}
                                    onFilePathSelect={handleAccountCustomerLogoUpdate}
                                />
                            </TableCell>
                        </TableRow>
                        {map(groupCustomerLogo, (group) => (
                            <TableRow key={group.groupId}>
                                <TableCell>
                                    <span>{group.groupName}</span>
                                </TableCell>
                                <TableCell>
                                    <FilesManagerInput
                                        filesManagerProps={{
                                            area: FileArea.ClientSession,
                                            ownerType: FileOwner.Group,
                                            ownerId: group.groupId
                                        }}
                                        inputProps={{
                                            name: `${group.groupId}-customer-logo`,
                                            value: group.image?.path || '',
                                            transparent: true,
                                            onChange: (value) => handleGroupCustomerLogoUpdate(group.groupId, value),
                                        }}
                                        onFilePathSelect={(value) => handleGroupCustomerLogoUpdate(group.groupId, value)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </Fragment>
                </TableBody>
            </Table>
            <UnsavedChangesLeavePrompt
                values={{account: accountCustomerLogo?.image?.path ?? '', groups: groupPaths}}
                initialValues={initialState}
            />
        </Fragment>
    );
};